import { commitIndexedDB, getIndexedDBData } from '@/assets/libs/indexedDBService.js'
import requestModule from '@/assets/libs/requestModule'
import QRCode from 'qrcode'

const USER_TICKETS_DBNAME = 'userTickets'

const getTicketsSignature = function (tickets) {
  const getFinishedItems = (status) => {
    return Object.values(tickets?.[status] || {})
      .flatMap(sale => (sale.items || []).map(item => ({
        saleSeq: sale.saleSeq,
        sequential: item.SEQUENTIAL,
        status: item.STATUS,
        redeemedTime: item.REDEEMED_TIME,
        operatorCode: item.OPERATOR_CODE
      })))
      .sort((a, b) => {
        return `${a.saleSeq}:${a.sequential}`.localeCompare(`${b.saleSeq}:${b.sequential}`)
      })
  }

  return JSON.stringify({
    available: getFinishedItems('available'),
    unavailable: getFinishedItems('unavailable'),
    pending: tickets?.pending || [],
    cancelled: tickets?.cancelled || []
  })
}

const sanitizeData = async function (data) {
  const sanitizedData = {}
  // itera sobre os status (available, pending, unavailable, cancelled)
  for (const [key, salesGroup] of Object.entries(data)) { // key = status, salesGroup = { '0000000991': [...items], ... }
    if (key !== 'pending' && key !== 'cancelled') {
      const groupedSales = []
      // itera sobre cada venda dentro do grupo (ex: '0000000991': [...items])
      for (const [saleSeq, items] of Object.entries(salesGroup)) {
        if (!items || items.length === 0) continue // venda sem itens, pula

        // processa todos os itens da venda para gerar os QR Codes
        const processedItems = await Promise.all(
          items
            ?.filter(item => item.QRCODE_KEY) // garante que o item tem um QR Code
            .map(async (item, index) => {
              return {
                ...item.DATA,
                STATUS: item.STATUS,
                // adiciona o número do item e o total para o display "Item: 1/2"
                itemNumber: index + 1,
                totalItemsInSale: items.length,
                // gera o QR Code
                QRCODE_KEY: await QRCode.toDataURL(btoa(item.QRCODE_KEY), { errorCorrectionLevel: 'L' })
              }
            })
        )

        // adiciona a venda agrupada ao array de vendas
        if (processedItems.length > 0) {
          const sortedItems = processedItems.sort((a, b) => a.SEQUENTIAL < b.SEQUENTIAL)

          const finalItems = sortedItems.map((item, index) => ({
            ...item,
            itemNumber: index + 1,
            totalItemsInSale: sortedItems.length
          }))

          groupedSales.push({
            saleSeq: saleSeq, // numero da venda
            items: finalItems,
            // data/hora do primeiro item para representar a venda
            orderTime: processedItems[0]?.ORDER_TIME
          })
        }
      }
      // ordena as vendas pela mais recente
      sanitizedData[key] = groupedSales.sort((a, b) => b.items[0].TIMESTAMP - a.items[0].TIMESTAMP)
    } else if (key === 'pending') {
      // pendentes -> apenas agrupa os itens
      sanitizedData[key] = Object.values(salesGroup).flat()
    }
  }
  // console.log('Sanitized Data:', sanitizedData)
  return sanitizedData
}

export default {
  state: {
    tickets: [],
    loading: false,
    error: null
  },
  mutations: {
    setTickets (state, tickets) {
      state.tickets = tickets
    },
    setLoading (state, loading) {
      state.loading = loading
    },
    setError (state, error) {
      state.error = error
    }
  },
  actions: {
    async loadTickets ({ commit, state }) {
      commit('setLoading', true)
      let tickets = []
      try {
        tickets = await getIndexedDBData(USER_TICKETS_DBNAME)
        if (getTicketsSignature(tickets) !== getTicketsSignature(state.tickets)) {
          commit('setTickets', tickets)
        }
      } catch (err) {
        console.log(err)
      } finally {
        try {
          const saleHistory = await requestModule.postRequest('/consumer/history', {}, null, 'get')
          const sanitizedData = await sanitizeData(saleHistory.data)
          if (getTicketsSignature(tickets) !== getTicketsSignature(sanitizedData)) {
            await commitIndexedDB(USER_TICKETS_DBNAME, sanitizedData)
            commit('setTickets', sanitizedData)
          }
        } catch (error) {
          console.log(error)
        }
        commit('setLoading', false)
      }
    },
    async clearTickets ({ commit }) {
      commit('setTickets', [])
      commit('setLoading', false)
      console.log('Clearing tickets from IndexedDB...')
      try {
        await commitIndexedDB(USER_TICKETS_DBNAME, {})
      } catch (error) {
        console.error('Error clearing tickets:', error)
      }
    }
  },
  getters: {
    getTickets: (state) => state.tickets,
    isLoading: (state) => state.loading
  }
}
